import React, { useState, useEffect, useCallback, memo } from 'react';

const MAX_RETRIES = 3;
const LOAD_TIMEOUT_MS = 12000;

/**
 * Inner iframe — memoized so it only re-renders when the URL actually changes.
 */
const PDFIframe = memo(({ url, onLoad, onError }) => (
  <iframe key={url} src={`${url}#toolbar=0`} className="pmv2-iframe" title="PDF viewer" onLoad={onLoad} onError={onError} />
));
PDFIframe.displayName = 'PDFIframe';

const PDFViewer = ({ document: docUrl, onLoad }) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Single effect: validate URL and set it.
  useEffect(() => {
    if (!docUrl) return;

    try {
      new URL(docUrl);
    } catch {
      setLoadError('Invalid PDF URL.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    const url = retryCount > 0 ? `${docUrl}?_r=${retryCount}` : docUrl;
    setPdfUrl(url);
  }, [docUrl, retryCount]);

  // Timeout guard — sets error without triggering another retry (avoids loops)
  useEffect(() => {
    if (!isLoading) return;
    const id = setTimeout(() => {
      setIsLoading(false);
      setLoadError('Loading timed out. The file may be too large or unavailable.');
    }, LOAD_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [isLoading]);

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    setLoadError(null);
    onLoad?.();
  }, [onLoad]);

  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setLoadError('Failed to load PDF.');
    if (retryCount < MAX_RETRIES) {
      setRetryCount((prev) => prev + 1);
    }
  }, [retryCount]);

  const handleManualRetry = useCallback(() => {
    setRetryCount((prev) => (prev >= MAX_RETRIES ? 0 : prev + 1));
    setLoadError(null);
  }, [retryCount]);

  if (!docUrl) return null;

  return (
    <div className="pdf-viewer">
      {isLoading && (
        <div className="viewer-loading">
          <div className="spinner-border text-primary" role="status" aria-label="Loading PDF">
            <span className="visually-hidden">Loading PDF…</span>
          </div>
        </div>
      )}

      {loadError && (
        <div className="viewer-error" role="alert">
          <i className="icon-warning me-2" />
          {loadError}
          <button type="button" className="btn btn-link ms-2 p-0" onClick={handleManualRetry}>
            Try again
          </button>
        </div>
      )}

      {pdfUrl && !loadError && <PDFIframe url={pdfUrl} onLoad={handleIframeLoad} onError={handleIframeError} />}
    </div>
  );
};

export default React.memo(PDFViewer);
